import React from 'react';

import {
  computePercentage,
  getSymbol,
  getVariation,
} from '../../../helpers/functions';
import { useSort } from '../../../hooks/useSort';
import { ColorButton, Icon } from '../../UI';
import { ProfitLoss } from '../../ProfitLoss';
import { Sort } from '../../NewStrategy/Sort';
import { Column } from '../../NewStrategy/StrategyTable';
import { StrategyAssetsGroup, TimeFrame } from '../types';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { TTickerCurrency } from '../../../types/model';
import { Asset } from '../../Asset/Asset';
import { logger } from '../../../helpers/logger';

const WrapperCol = ({
  children,
  className = '',
}: {
  children: JSX.Element;
  className: string;
}) => (
  <span
    className={`
      ${className}
      flex
      items-center
      w-full
      border-grey-10
      dark:border-grey-70
      bg-grey-5
      dark:bg-black-real
    `}
  >
    {children}
  </span>
);

export const COLUMNS_ASSETS_LIST = [
  'ASSET',
  'QUANTITY',
  'ENTRY PRICE',
  'LAST PRICE',
  'VARIATION',
  'TOTAL VALUE',
  'ALLOCATION',
] as const;

export const StrategyAssetsList = ({
  strategy: { assets, color, currency, title, totalValue },
  column = false,
  interval,
  strategyValue,
  widgetWidth,
  onClickArrowBack,
}: {
  strategy: StrategyAssetsGroup;
  column?: boolean;
  interval?: TimeFrame;
  strategyValue: number;
  widgetWidth: number;
  onClickArrowBack(): void;
}) => {
  const initialFieldSorting = 'ALLOCATION';
  const initialSortingDirection = 'sortDown';

  const { type, getSortValue, handleSort, direction } = useSort(
    [...COLUMNS_ASSETS_LIST],
    initialFieldSorting,
    initialSortingDirection
  );

  logger.log('WidgetWithPie:StrategyAssetList', {
    initialFieldSorting,
    initialSortingDirection,
    COLUMNS_ASSETS_LIST,
  });

  const symbol = getSymbol(currency!);

  // SORT
  const sortedAssets = assets.sort((a, b) => {
    const lastPriceA =
      a.asset.history[0][currency?.toLocaleLowerCase() as TTickerCurrency];
    const lastPriceB =
      b.asset.history[0][currency?.toLocaleLowerCase() as TTickerCurrency];
    const valueA = a.unit * lastPriceA;
    const valueB = b.unit * lastPriceB;

    let [arg1, arg2]: (number | string)[] = [a.name, b.name];
    type === 'QUANTITY' && ([arg1, arg2] = [a.unit, b.unit]);
    type === 'ENTRY PRICE' && ([arg1, arg2] = [a.entryPrice, b.entryPrice]);
    type === 'LAST PRICE' && ([arg1, arg2] = [lastPriceA, lastPriceB]);
    type === 'TOTAL VALUE' && ([arg1, arg2] = [valueA, valueB]);
    type === 'ALLOCATION' &&
      ([arg1, arg2] = [
        computePercentage(strategyValue, valueA),
        computePercentage(strategyValue, valueB),
      ]);

    if (type === 'VARIATION' && interval) {
      const historyA = a.asset.history;
      const historyB = b.asset.history;

      const varA = getVariation(
        currency!,
        historyA,
        a.purchasedAt,
        a.entryPrice,
        1
      )[interval];
      const varB = getVariation(
        currency!,
        historyB,
        b.purchasedAt,
        a.entryPrice,
        1
      )[interval];

      [arg1, arg2] = [
        computePercentage(varA, lastPriceA),
        computePercentage(varB, lastPriceB),
      ];
    }

    return getSortValue(arg1, arg2);
  });

  return (
    <div
      className={`
        border-t
        border-grey-10
        dark:border-transparent
        w-full
        grid
        grid-cols-${column ? 2 : COLUMNS_ASSETS_LIST.length}
      `}
    >
      <div
        className={`
          flex
          items-center
          gap-2
          min-w-32
          py-4
          w-full
          sticky
          left-0
          col-span-${column ? 2 : COLUMNS_ASSETS_LIST.length}
        `}
      >
        <Icon
          svgName="arrowBack"
          className="ml-5 cursor-pointer"
          onClick={onClickArrowBack}
        />
        <ColorButton radius={3} color={color!} isReadOnly={true} />
        <div className="font-medium capitalize">{title}</div>
      </div>
      {/* HEADER */}
      {!column &&
        COLUMNS_ASSETS_LIST.map((title, i) => (
          <div
            key={title}
            className={`
            w-full
            min-w-32
            py-2
            pl-5
            border-b
            border-t
            border-grey-10
            dark:border-grey-70
            ${
              !i &&
              `bg-white dark:bg-grey-90 sticky left-0 ${
                widgetWidth === 2 && 'border-r'
              }`
            }
          `}
          >
            <Column
              className={`
              flex
              items-center
              w-full
              h-full
            `}
              element={
                <Sort
                  className={`
                  flex
                  items-center
                  text-sm
                  font-medium
                  w-full
                  h-full
                `}
                  columnName={title}
                  key={title}
                  type={type}
                  handleSort={handleSort}
                  initialDirection={direction}
                />
              }
              span={1}
            />
          </div>
        ))}

      {/* DATA */}
      {sortedAssets.map((asset) => {
        const {
          asset: {
            id: assetId,
            logo,
            history,
            name: assetName,
            symbol: assetSymbol,
            type,
            exchange,
          },
          entryPrice,
          name,
          purchasedAt,
          unit,
          connectionType,
        } = asset;

        const lastPrice =
          history[0][currency!.toLocaleLowerCase() as TTickerCurrency];

        const assetVariation = getVariation(
          currency!,
          history, // series,
          purchasedAt,
          entryPrice,
          1
        )[interval ?? 'VAR_ALL'];

        return (
          <React.Fragment key={name}>
            {/* ASSET */}
            <WrapperCol
              className={`
                ${
                  !column
                    ? 'border-b'
                    : `col-span-${column ? 2 : COLUMNS_ASSETS_LIST.length}
                      border-t
                      border-grey-10
                    `
                }
                sticky
                left-0
                py-2
                ${widgetWidth === 2 && 'border-r'}
              `}
            >
              <div className={'ml-2'}>
                <Asset
                  type={type || ''}
                  name={assetName}
                  logo={logo}
                  symbol={assetSymbol}
                  connectionType={connectionType}
                  id={assetId}
                  exchange={exchange}
                />
              </div>
            </WrapperCol>

            {/* QUANTITY */}
            <span
              className={`flex ${
                column && 'flex-col bg-grey-5 dark:bg-black-real py-3'
              }`}
            >
              {column && (
                <span
                  className={`
                    text-xs
                    capitalize-first
                    pl-6
                    text-grey-50
                    font-extralight
                  `}
                >
                  {COLUMNS_ASSETS_LIST[1]}
                </span>
              )}
              <WrapperCol className={`pl-6 ${!column && 'border-b'}`}>
                <CustomNumber displayType="text" value={unit} />
              </WrapperCol>
            </span>

            {/* ENTRY PRICE */}
            <span
              className={`flex ${
                column && 'flex-col bg-grey-5 dark:bg-black-real py-3'
              }`}
            >
              {column && (
                <span
                  className={`
                    text-xs
                    capitalize-first
                    pl-6
                    text-grey-50
                    font-extralight
                  `}
                >
                  {COLUMNS_ASSETS_LIST[2]}
                </span>
              )}
              <WrapperCol className={`pl-6 ${!column && 'border-b'}`}>
                <CustomNumber
                  displayType="text"
                  suffix={symbol}
                  value={entryPrice}
                />
              </WrapperCol>
            </span>

            {/* LAST PRICE */}
            <span
              className={`flex ${
                column && 'flex-col bg-grey-5 dark:bg-black-real py-3'
              }`}
            >
              {column && (
                <span
                  className={`
                    text-xs
                    capitalize-first
                    pl-6
                    text-grey-50
                    font-extralight
                  `}
                >
                  {COLUMNS_ASSETS_LIST[3]}
                </span>
              )}
              <WrapperCol className={`pl-6 ${!column && 'border-b'}`}>
                <ProfitLoss
                  percentage={computePercentage(entryPrice, lastPrice, 100)}
                  value={lastPrice}
                  symbol={symbol}
                  displayType="text"
                />
              </WrapperCol>
            </span>

            {/* VARIATION */}
            <span
              className={`flex ${
                column && 'flex-col bg-grey-5 dark:bg-black-real py-3'
              }`}
            >
              {column && (
                <span
                  className={`
                    text-xs
                    capitalize-first
                    pl-6
                    text-grey-50
                    font-extralight
                  `}
                >
                  {COLUMNS_ASSETS_LIST[4]}
                </span>
              )}
              <WrapperCol className={`pl-6 ${!column && 'border-b'}`}>
                <ProfitLoss
                  percentage={computePercentage(assetVariation, lastPrice, 100)}
                  value={
                    computePercentage(assetVariation, lastPrice, 100) !== 0
                      ? lastPrice * unit - assetVariation * unit
                      : 0
                  }
                  symbol={symbol}
                  displayType="text"
                />
              </WrapperCol>
            </span>

            {/* TOTAL VALUE */}
            <span
              className={`flex ${
                column && 'flex-col bg-grey-5 dark:bg-black-real py-3 '
              }`}
            >
              {column && (
                <span
                  className={`
                    text-xs
                    capitalize-first
                    pl-6
                    text-grey-50
                    font-extralight
                  `}
                >
                  {COLUMNS_ASSETS_LIST[5]}
                </span>
              )}
              <WrapperCol className={`pl-6 ${!column && 'border-b'}`}>
                <ProfitLoss
                  percentage={computePercentage(entryPrice, lastPrice, 100)}
                  value={unit * lastPrice}
                  symbol={symbol}
                  displayType="text"
                />
              </WrapperCol>
            </span>

            {/* %ALLOCATION */}
            <span
              className={`flex ${
                column && 'flex-col bg-grey-5 dark:bg-black-real py-3'
              }`}
            >
              {column && (
                <span className="text-xs pl-6 text-grey-50 font-extralight">
                  {'Invested'}
                </span>
              )}
              <WrapperCol className={`pl-6 ${!column && 'border-b'}`}>
                <CustomNumber
                  value={computePercentage(strategyValue, unit * lastPrice)}
                  suffix="%"
                  displayType="text"
                />
              </WrapperCol>
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};
